/**
 * ユーザー情報のStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';
import {
  beginFetch,
  endFetch,
  failedFetch,
  FetchStatus
} from 'common/modules/app/fetch';
import { fetchFailure, ErrorResponse } from 'common/modules/app/errors';
import { ActionsUnion, createAction } from 'common/utils/action-utils';
import { getUserInfoRequest } from 'common/utils/request/sas-request';
import { GetUserResponse } from 'common/utils/request/sas-response';
import { request } from 'common/utils/request/http-request';
import { canonIdLogoutUrl, sasApiUrl } from 'common/utils/urls';
import { InnerError } from '../app/innerErrorCode';
import {
  deleteCookie,
  loadCookie,
  SasCookieKeys
} from 'common/utils/cookie-utils';
import { AppState } from 'pages';
import { beginLogout, LogoutStatus } from '../app/user';
import { loggedOut } from '../app/principal';

enum ActionTypes {
  RESET_STATE = 'domain/user/RESET_STATE',
  FETCH_SUCCESS = `domain/user/FETCH_SUCCESS`,
  CHANGE_SETTING = `domain/user/CHANGE_SETTING`
}

export enum UpdateKeyValue {
  KEY_ID = 'userId',
  KEY_NAME = 'name',
  KEY_LANGUAGE = 'language',
  KEY_COUNTRY = 'country',
  KEY_REGION = 'region'
}

const GET_USER_INFO_ID = 'get_user_info';
export const LOGIN_USER = 'login_user';
export const LOGOUT_USER = 'logout_user';

/**
 * ユーザー情報ををクリアする
 */
export const resetState = () => createAction(ActionTypes.RESET_STATE);

/**
 * ユーザー情報をを置き換える
 */
export const fetchSuccess = (payload: UserInformation) =>
  createAction(ActionTypes.FETCH_SUCCESS, payload);

/**
 * ユーザー情報をを置き換える
 */
export const changeSetting = (payload: updateInformation) =>
  createAction(ActionTypes.CHANGE_SETTING, payload);

const actions = {
  resetState,
  fetchSuccess,
  changeSetting
};
type UserAction = ActionsUnion<typeof actions>;

/**
 * ログインする
 * @param redirect リダイレクト先
 */
export const getLogin = (redirect = '') => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>
  ) => {
    // LOGIN_USERを処理中に変更
    dispatch(beginFetch(LOGIN_USER));

    // リダイレクト先が指定されなければ、window.location.hrefをリダイレクト先にする
    if (redirect === '') {
      redirect = window.location.href;
    }
    const redirectUrl = encodeURIComponent(redirect);
    // HEADで検証し、問題がなければwindow.login.hrefに指定する
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = function () {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status === 200) {
          // HEADの呼び出しが正常な場合は呼び出しを行う
          window.location.href = `${sasApiUrl}/v1/api/login?redirectUrl=${redirectUrl}&clientType=browser`;
        } else {
          // エラー処理
          const error = InnerError.loginFailed;
          error.statusCode = xhr.status;
          error.requestId = LOGIN_USER;
          dispatch(fetchFailure(error));
          dispatch(failedFetch(LOGIN_USER));
          return;
        }
      }
    };
    xhr.open(
      'head',
      `${sasApiUrl}/v1/api/login?redirectUrl=${redirectUrl}&clientType=browser`
    );
    xhr.send(null);
    // LOGIN_USERを未処理に変更
    dispatch(endFetch(LOGIN_USER));
  };
};

/**
 * ログアウトする
 * @param token SASのアクセストークン
 */
export const getLogout = (token: string) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // LOGOUT_USERを処理中に変更
    dispatch(beginFetch(LOGOUT_USER));

    // SASのアクセストークンなどがない状態ならば、CanonIDログアウトのみ実施
    const cookie = loadCookie();
    if (
      !cookie[SasCookieKeys.KEY_TOKEN] &&
      !cookie[SasCookieKeys.KEY_REFRESH_TOKEN]
    ) {
      window.location.href = canonIdLogoutUrl;
      return;
    }

    // HEADで検証し、問題がなければwindow.login.hrefに指定する
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = function () {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        const logoutStatus = getState().app.user.logoutStatus;
        if (logoutStatus === LogoutStatus.PROCESSING) {
          // 既にログアウト実施中ならば何もしない
          return;
        } else if (xhr.status === 200) {
          dispatch(beginLogout());
          // HEADの呼び出しが正常な場合は呼び出しを行う
          window.location.href = `${sasApiUrl}/v1/api/logout?token=${token}`;
        } else if (xhr.status === 403) {
          // トークン期限切れの場合は自前でCanonIDのログアウトを呼ぶ
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          window.location.href = canonIdLogoutUrl;
        } else {
          // エラー処理
          const error = InnerError.logoutFailed;
          error.statusCode = xhr.status;
          error.requestId = LOGOUT_USER;
          dispatch(fetchFailure(error));
          dispatch(failedFetch(LOGOUT_USER));
          return;
        }
      }
    };
    xhr.open('head', `${sasApiUrl}/v1/api/logout?token=${token}`);
    xhr.send(null);
    // LOGOUT_USERを未処理に変更
    dispatch(endFetch(LOGOUT_USER));
  };
};

/**
 * ユーザー情報を取得する
 */
export const getUser = (token: string) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // GET_USER_INFO_IDを処理中に変更
    dispatch(beginFetch(GET_USER_INFO_ID));
    // ユーザー情報取得APIを実行
    const requestData = getUserInfoRequest(token);
    const response = await request(
      requestData.url,
      requestData.config,
      false,
      false
    );
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = GET_USER_INFO_ID;
      if (error.statusCode && error.statusCode === 403) {
        // トークン期限切れの場合は再ログイン
        await dispatch(getLogin());
        // 再ログインに失敗した場合はエラー
        const status = getState().app.fetch[LOGIN_USER];
        if (status === FetchStatus.FAILED) {
          const innerError = InnerError.loginFailed;
          innerError.requestId = GET_USER_INFO_ID;
          dispatch(fetchFailure(innerError));
          dispatch(failedFetch(GET_USER_INFO_ID));
        }
        return;
      } else {
        if (error.statusCode && error.statusCode == 401) {
          // ログアウト状態ならば、Cookieを消してログアウト状態にする
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          dispatch(loggedOut());
        }
        dispatch(fetchFailure(error));
        dispatch(failedFetch(GET_USER_INFO_ID));
        return;
      }
    } else {
      // APIが成功した場合は取得したユーザー情報でstateを更新
      const body: GetUserResponse = (response.body as unknown) as GetUserResponse;
      // 取得したユーザー情報でstateを更新
      dispatch(fetchSuccess(body));
    }
    // GET_PLAN_LIST_IDを未処理に変更
    dispatch(endFetch(GET_USER_INFO_ID));
  };
};

/**
 * ユーザー情報を更新する
 * @param key: キー名
 * @param value: 値
 */
export const updateUser = (key: UpdateKeyValue, value: string) => {
  return (dispatch: ThunkDispatch<Record<string, unknown>, void, any>) => {
    // ユーザー情報を更新
    const updateInfo: updateInformation = {
      key: key,
      value: value
    };
    dispatch(changeSetting(updateInfo));
  };
};

/**
 * ユーザー情報をクリアする
 */
export const reset = () => {
  return (dispatch: ThunkDispatch<Record<string, unknown>, void, any>) => {
    dispatch(resetState());
  };
};

/**
 * ユーザー情報
 */
export interface UserInformation {
  /** 言語 */
  language: string;
  /** 国 */
  country: string;
  /** 地域 */
  region: string;
  /** メールアドレス */
  email: string;
}

interface updateInformation {
  /** 更新するkey名 */
  key: UpdateKeyValue;
  /** 更新する値 */
  value: string;
}

/**
 * ユーザー情報のinitialState
 */
const initialState: UserInformation = {
  language: '',
  country: '',
  region: '',
  email: ''
};

export default (
  state: UserInformation = initialState,
  actions: UserAction
): UserInformation => {
  switch (actions.type) {
    case ActionTypes.FETCH_SUCCESS:
      return actions.payload;
    case ActionTypes.CHANGE_SETTING:
      return { ...state, [actions.payload.key]: actions.payload.value };
    case ActionTypes.RESET_STATE:
      return initialState;
    default:
      return state;
  }
};
